"use client";

import {
	faChevronLeft,
	faChevronRight,
	faChevronsLeft,
	faChevronsRight,
	faPenRuler,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Typography, button, cn } from "@tosize/ui";
import { Fragment, useRef, useState } from "react";
import { Link } from "~/components/Link";
import Image from "~/components/utilities/Image";
import { useQuery } from "~/hooks/useQuery";

interface Props {
	defaultAmount: number;
	items: SlideType[];
	prevTitle?: string;
	nextTitle?: string;
	viewMessage: string;
}

export function ImpressionSlider(props: Props) {
	const { items, prevTitle, nextTitle, viewMessage, defaultAmount } = props;
	const parentRef = useRef<HTMLDivElement>(null);
	const [activeIndex, setActiveIndex] = useState(0);

	const isNotFirst = activeIndex > 0;
	const isNotLast = activeIndex + 1 < items.length;

	function scrollToIndex(index: number) {
		const element = parentRef.current?.children[index];

		if (element) {
			element.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" });
		}
	}

	const { router, createUrl } = useQuery();

	return (
		<div className="container">
			<div className="group relative">
				<div className="mx-[calc(-50vw+50%)]">
					<div
						ref={parentRef}
						className="scrollbar-none flex snap-x snap-mandatory flex-nowrap overflow-x-auto overflow-y-hidden"
						onScroll={(event) => {
							const width = event.currentTarget.firstElementChild?.clientWidth ?? 1024;
							const left = event.currentTarget.scrollLeft;
							const index = Math.round(left / width);

							// amount of half slides visible on screen
							const halfSlides =
								Math.abs(window.innerWidth / width) -
								Math.floor(Math.abs(window.innerWidth / width));

							const notVisiblePixelsHalfSlides = width - halfSlides * width;
							const container = (window.innerWidth - width) / 2;
							const margin = items.length * 32;
							const halfSlidesWidth = notVisiblePixelsHalfSlides + container;

							const slidesWidth = (items.length - 3) * width + halfSlidesWidth * 2 + margin;

							if (left === 0) {
								setActiveIndex(0);
							} else if (left === slidesWidth) {
								setActiveIndex(items.length - 1);
							} else if (index !== activeIndex) {
								setActiveIndex(index);
							}
						}}
					>
						{items.map((item, index) => (
							<Slide item={item} viewMessage={viewMessage} key={index} />
						))}
					</div>

					{items.length > 1 && (
						<>
							<button
								type="button"
								className="-translate-y-1/2 absolute top-1/2 left-2 no-touch:flex hidden h-10 w-10 appearance-none items-center justify-center rounded-full bg-shades-light hover:bg-primary hover:text-white focus:outline-none focus-visible:bg-primary focus-visible:text-white motion-safe:transition"
								onClick={() => scrollToIndex(isNotFirst ? activeIndex - 1 : items.length - 1)}
								title={prevTitle}
							>
								<FontAwesomeIcon icon={isNotFirst ? faChevronLeft : faChevronsLeft} />
							</button>

							<button
								type="button"
								className="-translate-y-1/2 absolute top-1/2 right-2 no-touch:flex hidden h-10 w-10 appearance-none items-center justify-center rounded-full bg-shades-light hover:bg-primary hover:text-white focus:outline-none focus-visible:bg-primary focus-visible:text-white motion-safe:transition"
								onClick={() => {
									scrollToIndex(activeIndex + 1);

									const url = createUrl({
										impression: Math.ceil((activeIndex + 2) / defaultAmount).toString(),
									});

									if (items.length === activeIndex + 2) {
										router.prefetch(url);
									}

									if (items.length === activeIndex + 1) {
										router.push(url, { scroll: false });
									}
								}}
								title={nextTitle}
							>
								<FontAwesomeIcon icon={isNotLast ? faChevronRight : faChevronsRight} />
							</button>
						</>
					)}
				</div>
			</div>
		</div>
	);
}

type SlideType = { src: string; alt: string; url?: string; description?: string };

function Slide({ item, viewMessage }: { item: SlideType; viewMessage: string }) {
	const className =
		"mx-2 relative flex overflow-hidden rounded-xl aspect-video w-full max-w-[min(1024px,calc(100%-4rem))] shrink-0 snap-center first:ml-4 last:mr-4 sm:mx-4";

	const content = (
		<Fragment>
			<Image
				skipAnimation
				src={item.src}
				alt={item.alt}
				width={1024}
				height={576}
				className="-z-1 aspect-video bg-shades-light"
			/>

			<div className="absolute right-0 bottom-0 left-0 hidden bg-gradient-to-t from-[rgba(70,70,70,0.7)] via-[rgba(70,70,70,0.57)] px-12 pt-12 pb-3 text-white md:flex lg:px-16 lg:pb-[18px] xl:px-[4.5rem] xl:pb-5">
				<div className="mt-auto flex w-full flex-col items-start gap-4">
					<div className="flex w-full items-center gap-4">
						<Typography
							variant="h4"
							component="span"
							color="text-white"
							starticon={faPenRuler}
							className="truncate leading-tight"
						>
							{item.alt}
						</Typography>
						<hr className="grow" />
					</div>

					{!!item.description && (
						<Typography color="text-white" className="line-clamp-2 leading-tight">
							{item.description}
						</Typography>
					)}

					{!!item.url && (
						<span className={button({ color: "white", variant: "outlined" })}>{viewMessage}</span>
					)}
				</div>
			</div>
		</Fragment>
	);

	if (item.url) {
		return (
			<Link
				href={item.url ?? "/"}
				target="_blank"
				rel="noopener"
				className={cn(
					className,
					"outline-none focus-visible:ring-1 focus-visible:ring-dark focus-visible:ring-inset",
				)}
			>
				{content}
			</Link>
		);
	}

	return <div className={className}>{content}</div>;
}
